import { React, useEffect, useState } from 'react';

import {
  Pane,
  Button,
  Popover,
  Position,
  Menu,
  ArrowTopRightIcon,
  ArrowBottomRightIcon,
  SwapHorizontalIcon,
} from 'evergreen-ui';
import BankCarousel from '../bank-carousel/BankCarousel';
import BalanceCardComponent from '../balance-cards/BalanceCardComponent';
import EntriesTableComponent from './EntriesTableComponent';
import { useQueryEntries, useQueryEntriesBankBalance, useQueryEntriesFiltered } from '../../services/api/entriesService';
import { useSelector } from 'react-redux';
import { mutate } from 'swr';
import { useQueryUserRolesCurrentRole } from '../../services/api/userRolesService';
import LoadingOverlay from '../../shared/LoadingOverlay';
import N8nChatWidget from '../chat/N8nChatWidget';

function EntryJournalsPage() {
  const companyId = localStorage.getItem('companyId');
  const userId = localStorage.getItem('userId');

  const { data: currentRole, isLoading: isLoadingCurrentRole } =
    useQueryUserRolesCurrentRole(
      { companyId: companyId, vitaUserId: userId },
      {
        revalidateIfStale: true,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
      }
    );

  // Fetch entries data for chat context
  const { data: entriesData, isLoading: isLoadingEntries } = useQueryEntries(companyId, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const { data: bankBalanceData, isLoading: isLoadingBankBalance } = useQueryEntriesBankBalance(companyId, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  // Get current month's data for chat context
  const currentDate = new Date();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  
  const { data: currentMonthEntries, isLoading: isLoadingCurrentMonth } = useQueryEntriesFiltered(
    companyId,
    firstDayOfMonth.toISOString().substring(0, 10),
    lastDayOfMonth.toISOString().substring(0, 10),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  // Filtered values states
  const [inputBalance, setInputBalance] = useState(0);
  const [outputBalance, setOutputBalance] = useState(0);
  const [transferInBalance, setTransferInBalance] = useState(0);
  const [transferOutBalance, setTransferOutBalance] = useState(0);

  // Prepare dashboard data for chat
  const prepareDashboardData = () => {
    if (!entriesData?.data || !bankBalanceData?.data) return null;

    const entries = entriesData.data;
    const bankBalances = bankBalanceData.data;
    const monthlyEntries = currentMonthEntries?.data || [];

    // Calculate summary statistics
    const totalEntries = entries.length;
    const monthlyEntriesCount = monthlyEntries.length;
    
    // Group entries by type
    const entriesByType = entries.reduce((acc, entry) => {
      const type = entry.entryType || 'unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    // Calculate monthly totals
    const monthlyTotals = monthlyEntries.reduce((acc, entry) => {
      const amount = parseFloat(entry.amount || 0);
      if (entry.entryType === 'income' || entry.entryType === 'receita') {
        acc.income += amount;
      } else if (entry.entryType === 'expense' || entry.entryType === 'despesa') {
        acc.expenses += amount;
      }
      acc.total += amount;
      return acc;
    }, { income: 0, expenses: 0, total: 0 });

    // Get recent entries (last 10)
    const recentEntries = entries
      .sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date))
      .slice(0, 10)
      .map(entry => ({
        id: entry.id,
        description: entry.description,
        amount: entry.amount,
        type: entry.entryType,
        date: entry.date || entry.createdAt,
        category: entry.category || entry.categoryName
      }));

    // Bank balances summary
    const totalBankBalance = bankBalances.reduce((sum, bank) => sum + parseFloat(bank.balance || 0), 0);

    return {
      summary: {
        totalEntries,
        monthlyEntriesCount,
        entriesByType,
        totalBankBalance,
        currentMonth: {
          income: monthlyTotals.income,
          expenses: monthlyTotals.expenses,
          netFlow: monthlyTotals.income - Math.abs(monthlyTotals.expenses),
          period: `${firstDayOfMonth.toISOString().substring(0, 10)} to ${lastDayOfMonth.toISOString().substring(0, 10)}`
        }
      },
      recentEntries,
      bankBalances: bankBalances.map(bank => ({
        name: bank.bankName || bank.name,
        balance: bank.balance,
        currency: bank.currency || 'BRL'
      })),
      lastUpdated: new Date().toISOString()
    };
  };

  const dashboardData = prepareDashboardData();

  if (isLoadingCurrentRole) {
    return <LoadingOverlay />;
  }

  return (
    <>
      <Pane
        className='EntryJournalsPage'
        display='flex'
        flexDirection='column'
        width='100%'
        height='100%'
        overflowY='scroll'
      >
        {currentRole?.data.userRole !== 'LANÇADOR' && (
          <Pane
            className='box1'
            display='flex'
            gap={10}
            padding={16}
            flexWrap='wrap'
            width='100%'
            height='fit-content'
            flexDirection='column'
          >
            <Pane
              className='maincards'
              // display='flex'
              // width='100%'
              // justifyContent='center'
              // flexDirection='row'
              gap={8}
            >
              <BalanceCardComponent />
              <BankCarousel />
            </Pane>
          </Pane>
        )}

        <Pane
          className='box2'
          display='flex'
          gap={10}
          flexDirection='column'
          flexWrap='nowrap'
          backgroundColor='white'
        >
          <EntriesTableComponent />
        </Pane>
      </Pane>
      
      {/* N8n Chat Widget - positioned in bottom right corner with dashboard data */}
      {/* <N8nChatWidget dashboardData={dashboardData} /> */}
    </>
  );
}

export default EntryJournalsPage;
